<template>
  <AppDialog :is-open="isDialogOpened"
             :data-locator="preventRedactPaneClose ? 'save-modifications-dialog' : 'save-dialog'"
             :hideFooter="redactDocumentPending"
             :closeButton="!redactDocumentPending"
             size="l"
             @cancel="closeDialog"
  >
    <template v-if="!redactDocumentPending" #title>
      {{ $t(`${redactDialogTranslationKey}.title`) }}
    </template>

    <template #body>
      <p class="my-4" v-if="!redactDocumentPending">
        {{ $t(`${redactDialogTranslationKey}.description`) }}
      </p>

      <div v-if="redactDocumentPending" class="text-center">
        <AppLoader small color="info-darken-1" data-locator="saving-loader"></AppLoader>
        <h4 class="mt-6">
          {{ $t('project.documents.DocumentViewer.RedactDocumentDialog.savingInProgress') }}
        </h4>
        <p class="my-6">
          {{ $t('project.documents.DocumentViewer.RedactDocumentDialog.actionCanTakeSomeTime') }}
        </p>
      </div>
    </template>

    <template v-if="!redactDocumentPending" #footer>
      <app-button
        data-locator="cancel-button"
        class="mr-2"
        type="outlined"
        variant="neutral"
        @click="closeDialog"
      >
        {{ preventRedactPaneClose
          ? $t('project.documents.DocumentViewer.RedactDocumentDialog.keepRedacting')
          : $t('common.cancel')
        }}
      </app-button>

      <app-button
        v-if="preventRedactPaneClose"
        data-locator="quit-without-saving-button"
        type="primary"
        @click="quitWithoutSaving"
      >
        {{ $t('project.documents.DocumentViewer.RedactDocumentDialog.quitWithoutSaving') }}
      </app-button>

      <app-button
        v-else
        data-locator="confirm-button"
        @click="prepareRedactDocument"
      >
        {{ $t('common.register') }}
      </app-button>
    </template>
  </AppDialog>
</template>

<script>
import { mapActions } from 'vuex'

import AppLoader from '@/common/AppLoader.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import { track } from '@/common/pendo/agent'
import { RedactionNotifications } from '@/common/utils/notifications.ts'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import { realtimeService } from '@/services/realtime.service.ts'
import { DocumentViewerActionTypes } from '@/store/modules/document-viewer/action_types'
import { FilesActionTypes } from '@/store/modules/files/action_types'

export default {
  name: 'RedactDocumentDialog',
  components: { AppDialog, AppLoader },
  mixins: [dialogMixin],
  emits: [
    'confirm',
    'quit-without-saving',
    'polling-error',
  ],
  props: {
    pdfAppInstance: {
      type: Object,
      required: true,
    },
    mnemo: {
      type: String,
      required: true,
    },
    fileId: {
      type: [String, Number],
      required: true,
    },
    redactedZones: {
      type: Array,
      required: true,
    },
    isRedactByZone: {
      type: Boolean,
      default: false,
    },
    preventRedactPaneClose: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pdfContainer: this.pdfAppInstance.pdfViewer.container,
      redactDocumentPending: false,
      pollingInterval: null,
    }
  },
  computed: {
    redactDialogTranslationKey () {
      return `project.documents.DocumentViewer.RedactDocumentDialog${this.preventRedactPaneClose ? '.quit' : ''}`;
    },
  },
  methods: {
    ...mapActions('files', [FilesActionTypes.GET_FILE]),
    ...mapActions('documentViewer', [DocumentViewerActionTypes.REDACT_DOCUMENT]),
    async prepareRedactDocument () {
      this.redactDocumentPending = true
      this.$emit('polling-error', null)

      try {
        await this.REDACT_DOCUMENT({
          mnemo: this.mnemo,
          fileId: this.fileId,
          data: {
            rectanglePositions: this.convertCanvasRectanglesToPdf(),
          },
        })
        if (this.isRedactByZone) {
          track('redact_by_zone')
        } else {
          track('redact_by_keywords')
        }

        // Start polling after the document is sent for redaction
        // Because the redaction process is asynchronous on the server
        this.startListeningToRedactionEvents()
      } catch (error) {
        // No toast here because the error message is displayed in the parent's template
        console.error(error)
        this.redactDocumentPending = false
        this.closeDialog()
      }
    },
    startListeningToRedactionEvents() {
      realtimeService.on([RedactionNotifications.REDACTION_FINISHED, RedactionNotifications.REDACTION_ERROR], this.handleRedactionEvents)
    },
    handleRedactionEvents (event, data) {
      if (data.file != this.fileId) {
        return
      }

      if (event === RedactionNotifications.REDACTION_FINISHED) {
        toast.success(this.$t('project.documents.DocumentViewer.RedactDocumentDialog.redactDocumentSuccess'))
        this.$emit('confirm')
      } else if (event === RedactionNotifications.REDACTION_ERROR) {
        console.error('Redaction failed on the server')
        this.$emit('polling-error', true)
      }

      realtimeService.off([RedactionNotifications.REDACTION_FINISHED, RedactionNotifications.REDACTION_ERROR], this.handleRedactionEvents)
      this.redactDocumentPending = false
      this.closeDialog()
    },
    convertCanvasRectanglesToPdf () {
      this.createTemporaryCanvases()

      const convertedZones = this.redactedZones.map(rect => {
        const pageElement = this.pdfContainer.querySelector(`.page[data-page-number="${rect.page}"]`)
        if (!pageElement) return null

        const canvas = pageElement.querySelector('.annotation-canvas')
        if (!canvas) return null

        // Retrieve dimensions of the PdfViewer and the canvas
        const pdfViewerPage = this.pdfAppInstance.pdfViewer.getPageView(rect.page - 1)
        const viewport = pdfViewerPage.pdfPage.getViewport({ scale: 1 })
        const pdfWidth = viewport.width
        const pdfHeight = viewport.height
        const canvasWidth = canvas.width
        const canvasHeight = canvas.height

        // Convert canvas coordinates to Pdf coordinates
        const scaleFactorX = pdfWidth / canvasWidth
        const scaleFactorY = pdfHeight / canvasHeight

        const x = rect.x * canvasWidth * scaleFactorX
        const y = rect.y * canvasHeight * scaleFactorY

        return {
          page: rect.page,
          x: Math.max(x, 0),
          y: Math.max(y, 0),
          endX: (rect.x + rect.width) * canvasWidth * scaleFactorX,
          endY: (rect.y + rect.height) * canvasHeight * scaleFactorY,
        }
      }).filter(Boolean)

      this.removeTemporaryCanvases()

      return convertedZones
    },
    createTemporaryCanvases() {
      // Create a canvas for each page that doesn't have one
      // This is necessary because the canvas is removed when the page is not visible
      // and we need it to convert the rectangles to Pdf coordinates
      const pages = this.pdfContainer.querySelectorAll('.page')
      pages.forEach((pageElement) => {
        if (!pageElement.querySelector('.annotation-canvas')) {
          const canvas = document.createElement('canvas')
          canvas.className = 'annotation-canvas'
          canvas.style.position = 'absolute'
          canvas.style.top = '0'
          canvas.style.left = '0'
          canvas.width = pageElement.clientWidth
          canvas.height = pageElement.clientHeight

          pageElement.appendChild(canvas)
        }
      })
    },
    removeTemporaryCanvases() {
      const pages = this.pdfContainer.querySelectorAll('.page')
      pages.forEach((pageElement) => {
        const boundingRect = pageElement.getBoundingClientRect()
        // Check if page is not visible in viewport and remove the canvas
        const isPageVisible = boundingRect.top < this.pdfContainer.clientHeight && boundingRect.bottom > 0
        if (!isPageVisible) {
          const canvas = pageElement.querySelector('.annotation-canvas')
          if (canvas) {
            pageElement.removeChild(canvas)
          }
        }
      })
    },
    quitWithoutSaving () {
      this.$emit('quit-without-saving')
      this.closeDialog()
    },
  },
}
</script>
